import React from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { FormControl, MenuItem, Select, Skeleton } from "@mui/material";

import {
    PageNavButton,
    PageNumberButton,
    PageSizeBox,
    PageSizeLabel,
    PaginationCenterSection,
    PaginationControls,
    PaginationEllipsis,
    PaginationInfoSection,
    PaginationSizeSection,
    PaginationWrap,
    ResultsText,
} from "./TablePagination.styled";
import { TablePaginationProps } from "./TablePagination.types";

const MAX_VISIBLE_PAGES = 5;

const TablePagination = ({
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
    standalone = false,
    loading = false,
}: TablePaginationProps) => {
    if (loading) {
        return (
            <PaginationWrap $standalone={standalone}>
                <PaginationInfoSection>
                    <Skeleton variant="text" width={180} height={20} />
                </PaginationInfoSection>
                <PaginationCenterSection>
                    <PaginationControls>
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                width={30}
                                height={30}
                                sx={{ borderRadius: "8px" }}
                            />
                        ))}
                    </PaginationControls>
                </PaginationCenterSection>
                <PaginationSizeSection>
                    {onPageSizeChange && (
                        <Skeleton
                            variant="rectangular"
                            width={120}
                            height={30}
                            sx={{ borderRadius: "8px" }}
                        />
                    )}
                </PaginationSizeSection>
            </PaginationWrap>
        );
    }

    const safeTotalPages = Math.max(1, totalPages);
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const goTo = (page: number) => {
        if (page < 1 || page > safeTotalPages || page === currentPage) return;
        onPageChange(page);
    };

    const renderPageNumbers = () => {
        const pages: React.ReactNode[] = [];
        const page = (i: number) => (
            <PageNumberButton
                key={i}
                $active={currentPage === i}
                onClick={() => goTo(i)}
            >
                {i}
            </PageNumberButton>
        );

        if (safeTotalPages <= MAX_VISIBLE_PAGES) {
            for (let i = 1; i <= safeTotalPages; i++) pages.push(page(i));
            return pages;
        }

        pages.push(page(1));

        if (currentPage > 3) {
            pages.push(
                <PaginationEllipsis key="ellipsis-start">
                    <MoreHorizIcon fontSize="small" />
                </PaginationEllipsis>,
            );
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(safeTotalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) {
            if (i !== 1 && i !== safeTotalPages) pages.push(page(i));
        }

        if (currentPage < safeTotalPages - 2) {
            pages.push(
                <PaginationEllipsis key="ellipsis-end">
                    <MoreHorizIcon fontSize="small" />
                </PaginationEllipsis>,
            );
        }

        pages.push(page(safeTotalPages));

        return pages;
    };

    return (
        <PaginationWrap $standalone={standalone}>
            <PaginationInfoSection>
                {totalItems > 0 && (
                    <ResultsText>
                        Showing <strong>{startItem}</strong> to{" "}
                        <strong>{endItem}</strong> of{" "}
                        <strong>{totalItems}</strong> results
                    </ResultsText>
                )}
            </PaginationInfoSection>

            <PaginationCenterSection>
                <PaginationControls>
                    <PageNavButton
                        $disabled={currentPage === 1}
                        onClick={() => goTo(currentPage - 1)}
                        title="Previous page"
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </PageNavButton>

                    {renderPageNumbers()}

                    <PageNavButton
                        $disabled={currentPage === safeTotalPages}
                        onClick={() => goTo(currentPage + 1)}
                        title="Next page"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </PageNavButton>
                </PaginationControls>
            </PaginationCenterSection>

            <PaginationSizeSection>
                {onPageSizeChange && (
                    <PageSizeBox>
                        <PageSizeLabel>Show</PageSizeLabel>
                        <FormControl size="small">
                            <Select
                                value={pageSize}
                                displayEmpty
                                onChange={(e) =>
                                    onPageSizeChange(Number(e.target.value))
                                }
                            >
                                {pageSizeOptions.map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <PageSizeLabel>per page</PageSizeLabel>
                    </PageSizeBox>
                )}
            </PaginationSizeSection>
        </PaginationWrap>
    );
};

export default TablePagination;
